import './ProductDetails.css';
import React, { useState } from 'react';
import { ProductHeader } from './ProductHeader';
import { ProductGeneralInfo } from './ProductGeneralInfo';
import { Sales } from './Sales';
import { Content } from './Content';
import { Grid } from '../../../components/Grid/Grid';
import { Container } from '../../../components/Container/Container';
import { ProductVariants } from './ProductVariants';
import { Stocks } from './Stocks';
import { Pricing } from './Pricing';
import { useRemoteData } from '../../../model/data';
import { getProduct, getProductName } from '../../../model/product';
import { useRouterParam } from '../../../model/history';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { useMeta, getPageTitle } from '../../../model/meta';
import { productsPath } from '../../../routes';
import { Activities, ActivitiesContext } from '../../../components/Activities/Activities';
import { t } from '../../../locales/translate';
import { MusicCharts } from './MusicCharts/MusicCharts';
import { useReloadKey } from '../../../util/util';
import { Stack } from '../../../components/Stack/Stack';
import { CheckoutOptions } from '../../ticketing/Events/Event/Shop/CheckoutOptions';
import { ShopCategories } from '../../ticketing/Events/Event/Shop/ShopCategories';
import { Meta } from '../../../components/meta/meta';
import { getProductCategories } from '../../../model/category';
import { handleError } from '../../../model/notification';
import { SalesHeatmap } from '../../ticketing/Events/Event/Overview/SalesHeatmap/SalesHeatmap';
import { WarningCard } from '../components/WarningCard';
import { BookCharts } from './book_charts/book_charts';
import { SalesOverTime } from '../../ticketing/Events/Event/Overview/sales_over_time/sales_over_time';
import { SalesByGender } from '../../ticketing/Events/Event/Overview/sales_by_gender/sales_by_gender';
import { UtmTrackingSourcesCard } from '../../../components/product_utm_tracking/utm_tracking_sources_card';
import { UtmTrackingMediaCard } from '../../../components/product_utm_tracking/utm_tracking_media_card';
import { UtmTrackingCampaignsCard } from '../../../components/product_utm_tracking/utm_tracking_campaigns_card';

export function ProductDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [productsKey, reloadProducts] = useReloadKey();
  const [product, loading, error] = useRemoteData(
    async () => {
      await getProduct(id);
    },
    (db) => {
      return db.table('products').where({ slug: id })[0] || db.table('products').where({ id })[0];
    },
    [id, productsKey.toString()]
  );

  useMeta({
    title: getPageTitle(loading, getProductName(product), product),
    breadcrumbs: [
      { title: t.model.product._plural, url: productsPath() },
      {
        title: getPageTitle(loading, getProductName(product), product),
      },
    ],
  });

  const [activitiesKey, reloadActivities] = useReloadKey();
  const [productImportantActivitiesKey, reloadProductImportantActivities] = useReloadKey();

  const [loadingProduct, setLoadingProduct] = useState(false);

  async function handleOnCategoriesUpdate(): Promise<void> {
    setLoadingProduct(true);
    try {
      await getProductCategories(product.id);
    } catch (error) {
      handleError(error);
    }
    setLoadingProduct(false);
  }

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.product._name)} />;
  } else if (error || !product) {
    return <Container>{t.errors.itemNotFound(t.model.product._name)}.</Container>;
  }

  return (
    <ActivitiesContext.Provider value={{ reloadActivities }}>
      <Container className="company-details">
        <Stack size="lg">
          {!product.material && product.status === 'on_sale' && (
            <WarningCard>{t.model.product.materialMissingWarning}</WarningCard>
          )}
          <ProductHeader product={product} />
          <Activities
            className="product-activities"
            title={t.model.product.importantCommentsTitle}
            readOnly
            subjectType="merch_product"
            subjectId={product.id}
            metaFilter="productImportant"
            cacheKey={productImportantActivitiesKey}
            collapseWhenEmpty
            collapseWhenLoading
          />
          <Grid>
            <ProductGeneralInfo product={product} />
            <ShopCategories
              title={t.model.shop.shopsAndCategories}
              product={product}
              blacklist={['tour']}
              loading={loadingProduct}
              onChange={() => handleOnCategoriesUpdate()}
            />
          </Grid>
          <Grid>
            <Content product={product} locale="De" reloadProducts={reloadProducts} />
            <Content product={product} locale="En" reloadProducts={reloadProducts} />
          </Grid>
          <ProductVariants product={product} />
          <Pricing product={product} onPriceChange={() => reloadActivities()} />
          <Stocks variants={product.productVariants} />
          <Grid>
            <MusicCharts product={product} />
            <BookCharts product={product} />
            <CheckoutOptions product={product} productType="product" />
          </Grid>
          {product.status !== 'draft' && (
            <>
              <Sales product={product} />
              <SalesOverTime saleType="product" title={t.model.sale._plural} saleId={product.id} />
              <SalesByGender saleType="product" saleId={product.id} />
              <SalesHeatmap type="product" saleId={product.id} />
              <UtmTrackingSourcesCard productId={product.id} />
              <UtmTrackingMediaCard productId={product.id} />
              <UtmTrackingCampaignsCard productId={product.id} />
            </>
          )}
          <Meta id={product.id} type="product" />
          <Activities
            subjectType="merch_product"
            subjectId={product.id}
            cacheKey={activitiesKey}
            onFlag={reloadProductImportantActivities}
          />
        </Stack>
      </Container>
    </ActivitiesContext.Provider>
  );
}
